/**
 * The core: one delegated listener, one menu at a time.
 *
 * Everything the library actually decides happens in the pure modules around
 * this one. The registry answers which definition owns a click, the resolver
 * turns items into plain values, the renderer turns those into DOM and the
 * placement engine turns numbers into coordinates. This module owns the order
 * those run in, the lifetime of the element they produce, and nothing else.
 *
 * The open sequence below is a contract, not an implementation detail: the end
 * to end suite asserts the event order it produces, so the steps are numbered
 * to match the plan and must not be reordered for convenience.
 */

import { Emitter } from './Emitter'
import type { Listener } from './Emitter'
import { Keyboard } from './Keyboard'
import type { KeyboardHandlers } from './Keyboard'
import { place } from './Placement'
import { Registry } from './Registry'
import { renderError, renderLoading, renderMenu } from './Renderer'
import { resolveItems, resolveLabel } from './Resolver'
import { inTriangle } from './SafeTriangle'
import type {
  CloseReason,
  ContextMenuOptions,
  Ctx,
  EventName,
  EventPayload,
  Item,
  ItemsResolver,
  MenuDefinition,
  Meta,
  Rect,
  ResolvedItem,
} from './types'

/** The gap between the pointer and the menu, when nothing overrides it. */
const DEFAULT_OFFSET = { x: 2, y: 2 } as const

/** How long a touch must be held before the menu opens. */
const DEFAULT_LONG_PRESS = 500

/** High enough to sit above the usual application chrome, low enough to leave headroom. */
const DEFAULT_Z_INDEX = 2147483000

/** How far a finger may travel during a long press before it counts as a drag. */
const LONG_PRESS_SLOP = 10

/**
 * How many times the microtask queue is drained before an unsettled `items`
 * resolver earns a loading panel.
 *
 * A resolver that already has its data settles in one tick, and a couple of
 * `await`s over cached values settle within four, so neither flashes a panel
 * nobody had time to read. Anything that reaches the network or a timer needs
 * a macrotask, so it never settles here and always gets the panel.
 */
const MICROTASK_GRACE = 4

/**
 * How long the pointer must rest on a submenu row before its panel opens.
 *
 * Short enough to feel immediate on the row the user meant, long enough that
 * dragging the pointer down a list does not open every panel it passes.
 */
const SUBMENU_OPEN_DELAY = 120

/**
 * How long an open submenu survives after the pointer leaves the row that owns
 * it. The safe triangle holds this off while the pointer is heading for the
 * panel, so it only has to cover the moment of leaving.
 */
const SUBMENU_CLOSE_DELAY = 200

/** What a menu is doing right now. Only `open` announces itself with events. */
type Phase = 'pending' | 'open' | 'error'

/** One open submenu panel and everything needed to take it down again. */
interface Submenu {
  /** The row, one level up, that owns this panel. */
  parentRow: HTMLElement
  /** The resolved row, so `submenu:close` can name what closed. */
  item: ResolvedItem
  menu: HTMLElement
  items: ResolvedItem[]
  keyboard: Keyboard
  usedPopover: boolean
}

/** Everything one open owns, from the first resolver call to the last event. */
interface MenuState {
  /** Invalidated by any newer open or by a close, so stale async work can bail. */
  token: number
  phase: Phase
  def: MenuDefinition
  el: HTMLElement
  meta: Meta
  ctx: Ctx
  /** The event that triggered this open, when there was one. */
  event: Event | undefined
  anchor: { x: number; y: number }
  offset: { x: number; y: number }
  rtl: boolean
  /** Null until something is mounted: a pending resolver may not have shown anything yet. */
  menu: HTMLElement | null
  items: ResolvedItem[]
  usedPopover: boolean
  restoreFocus: HTMLElement | null
  /** Null for the loading and error panels, which take no keys. */
  keyboard: Keyboard | null
  /** The open submenu chain. Index 0 is the panel opened from the root. */
  submenus: Submenu[]
}

/** A close waiting on the pointer. `timer` is null while the triangle holds it. */
interface PendingClose {
  /** The shallowest level to close, counting the root as 0. */
  from: number
  timer: ReturnType<typeof setTimeout> | null
}

/** Normalise `string | string[] | undefined` into a list of class names. */
function classList(value: string | string[] | undefined): string[] {
  if (value === undefined) return []

  return (Array.isArray(value) ? value : [value]).filter((entry) => entry.length > 0)
}

/**
 * The writing direction at the trigger, which `place` needs and cannot work out
 * for itself.
 *
 * The nearest `dir` attribute is authoritative and is checked first, because it
 * is what an application actually sets and because a computed style is not
 * available in every environment the core runs in. CSS `direction` is the
 * fallback for a document that styles the direction instead of marking it up.
 */
function isRtl(el: HTMLElement): boolean {
  const marked = el.closest('[dir]')
  const dir = marked?.getAttribute('dir')?.toLowerCase()

  if (dir === 'rtl') return true
  if (dir === 'ltr') return false

  const view = el.ownerDocument.defaultView

  return view?.getComputedStyle(el).direction === 'rtl'
}

/** The visible area the menu must stay inside. */
function viewportOf(el: HTMLElement): { width: number; height: number } {
  const view = el.ownerDocument.defaultView

  return { width: view?.innerWidth ?? 0, height: view?.innerHeight ?? 0 }
}

/** Where a menu anchored on an element rather than a point starts. */
function anchorOf(el: HTMLElement, rtl: boolean): { x: number; y: number } {
  const rect = el.getBoundingClientRect()

  return { x: rtl ? rect.right : rect.left, y: rect.bottom }
}

/**
 * The point a trigger event asks for.
 *
 * A keyboard invoked `contextmenu` (Shift+F10, the menu key) carries no useful
 * coordinates, so the element itself becomes the anchor.
 */
function pointOf(event: Event, el: HTMLElement, rtl: boolean): { x: number; y: number } {
  if (event instanceof MouseEvent && (event.clientX > 0 || event.clientY > 0)) {
    return { x: event.clientX, y: event.clientY }
  }

  return anchorOf(el, rtl)
}

/** The action of a resolved row, if it has one. */
function actionOf(item: ResolvedItem): ((meta: Meta, ctx: Ctx) => void | Promise<void>) | undefined {
  if (item.kind === 'separator' || item.kind === 'header' || item.kind === 'custom') return undefined

  return 'action' in item.source ? item.source.action : undefined
}

/**
 * Put the menu on screen, in the top layer when the engine has one.
 *
 * Returns whether the Popover API was used, which is what decides how it comes
 * off screen again. The fallback is a plain fixed element with a very high
 * z-index: it loses the top layer, so a transformed or clipping ancestor can
 * still affect it, but it never throws and it never leaves the menu invisible.
 */
function showMenu(menu: HTMLElement, zIndexFallback: number): boolean {
  if (typeof menu.showPopover === 'function') {
    try {
      menu.showPopover()

      return true
    } catch (error) {
      console.error('[context-menu] showPopover failed, falling back to a fixed position', error)
    }
  }

  // The attribute goes too: an engine with no Popover API ignores it, and one
  // with a partial implementation must not hide the menu with the user agent
  // rule that keeps a closed popover out of the layout.
  menu.removeAttribute('popover')
  menu.dataset['fallback'] = 'true'
  menu.style.position = 'fixed'
  menu.style.zIndex = String(zIndexFallback)

  return false
}

/**
 * The viewport coordinates a pointer event carries, or null when it carries
 * none.
 *
 * Read structurally rather than through `instanceof MouseEvent`, because the
 * class a host gives `pointermove` is not something the core should depend on,
 * and a synthetic event that carries the two numbers is just as usable.
 */
function pointerPointOf(event: Event): { x: number; y: number } | null {
  const candidate = event as { clientX?: unknown; clientY?: unknown }

  if (typeof candidate.clientX !== 'number' || typeof candidate.clientY !== 'number') return null

  return { x: candidate.clientX, y: candidate.clientY }
}

/**
 * The key an `attach` marker attribute turns into.
 *
 * The attribute is `data-ctx-1`, and the dataset algorithm camel cases a dash
 * only when an ASCII **lowercase letter** follows it. A dash followed by a
 * digit therefore survives, and the key arrives as the literal `ctx-1`. jQuery
 * derives `.data()` keys by the same rule, which is why one pattern serves both
 * sources and why this lives here rather than being written out again in each
 * adapter.
 */
const ATTACH_MARKER_KEY = /^ctx-\d+$/

/**
 * A copy of `data` with the library's own `attach` bookkeeping removed.
 *
 * The marker is how a per element registration is expressed as a delegated
 * selector. It is not the consumer's data and has no business reaching a
 * predicate as a meta key.
 */
export function stripAttachMarkers<T>(data: Record<string, T>): Record<string, T> {
  const kept: Record<string, T> = {}

  for (const [key, value] of Object.entries(data)) {
    if (!ATTACH_MARKER_KEY.test(key)) kept[key] = value
  }

  return kept
}

/**
 * The default `resolveMeta`: the element's data attributes, minus the marker
 * `attach` writes.
 *
 * Exported because the framework adapters fall back to exactly this when a
 * consumer supplies neither a `meta` object nor a `resolveMeta` of their own.
 * One function, so the dataset is read the same way everywhere.
 */
export function datasetMeta(el: HTMLElement): Meta {
  return stripAttachMarkers<string | undefined>({ ...el.dataset })
}

/** A measured element as the plain rect the pure modules take. */
function rectOf(el: HTMLElement): Rect {
  const rect = el.getBoundingClientRect()

  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
}

/** The `items` of a resolved row, when it has any. */
function submenuItemsOf(item: ResolvedItem): Item[] | ItemsResolver | undefined {
  if (item.kind !== 'item') return undefined

  return 'items' in item.source ? item.source.items : undefined
}

/** Take the menu off screen. The element is removed by the caller either way. */
function hideMenu(menu: HTMLElement, usedPopover: boolean): void {
  if (!usedPopover) return

  try {
    menu.hidePopover()
  } catch {
    // Already hidden, which is not a failure: the element is removed next.
  }
}

/**
 * Marker counter for `attach`. Module level, not per instance: two instances
 * both starting at zero would mint the same marker, and each instance's
 * delegated listener would then match the other instance's element.
 */
let attachCount = 0

export class ContextMenu {
  readonly #registry = new Registry()
  readonly #emitter = new Emitter()
  readonly #root: HTMLElement | Document
  readonly #offset: { x: number; y: number }
  readonly #closeOnScroll: boolean
  readonly #longPress: number | false
  readonly #className: string[]
  readonly #zIndexFallback: number

  /** The disposer of every live `attach`, so `destroy` can undo the markers. */
  readonly #attached = new Set<() => void>()

  #state: MenuState | null = null
  #token = 0
  #idCount = 0
  #destroyed = false
  #dismissalDocument: Document | null = null
  #cancelLongPress: (() => void) | null = null

  /**
   * The last pointer position seen over the open menu chain.
   *
   * Sampling rule: `pointermove` is listened to on the open panels only, never
   * on the document. The only path that matters is the one across the menu, and
   * a document-wide listener would fire for every pixel of unrelated movement
   * for as long as a menu is open. The cost is that the position can be one
   * event stale if the pointer leaves the chain entirely, which is exactly the
   * case where the submenu should close anyway.
   */
  #pointer = { x: 0, y: 0 }

  /**
   * The tip of the safe triangle: where the pointer was when the open submenu
   * was committed, refreshed while the pointer is still on the row that owns
   * it. That keeps the wedge anchored to the row the traverse starts from.
   */
  #triangleOrigin = { x: 0, y: 0 }

  /** A submenu open whose resolver has not come back yet, and the row it is for. */
  #pendingOpen: { row: HTMLElement; token: number } | null = null

  /** The row a hover has scheduled a submenu open for, and the panel it is in. */
  #pendingHover: { row: HTMLElement; depth: number } | null = null

  /**
   * The row the pointer is currently over, so `highlight` fires once per row
   * entered rather than once per descendant span crossed inside it.
   */
  #hoverRow: HTMLElement | null = null

  #submenuOpenTimer: ReturnType<typeof setTimeout> | null = null
  #pendingClose: PendingClose | null = null
  /** Bumped by anything that supersedes a submenu open still in flight. */
  #submenuToken = 0

  constructor(options: ContextMenuOptions = {}) {
    this.#root = options.root ?? document
    this.#offset = options.offset ?? DEFAULT_OFFSET
    this.#closeOnScroll = options.closeOnScroll ?? true
    this.#longPress = options.longPress ?? DEFAULT_LONG_PRESS
    this.#className = classList(options.className)
    this.#zIndexFallback = options.zIndexFallback ?? DEFAULT_Z_INDEX

    // One listener for the whole instance, installed once and never again.
    // Registering a definition adds a row to the registry, never a listener.
    this.#root.addEventListener('contextmenu', this.#onContextMenu)

    if (this.#longPress !== false) this.#root.addEventListener('pointerdown', this.#onTouchStart)
  }

  /** True while a menu, a loading panel or an error panel is on screen. */
  get isOpen(): boolean {
    return this.#state !== null && this.#state.menu !== null
  }

  /** Register a delegated definition. The disposer removes only this one. */
  register(def: MenuDefinition): () => void {
    return this.#registry.add(def)
  }

  /**
   * Register a definition for one element that has no selector worth writing.
   *
   * The element is marked with a unique attribute and the definition selects on
   * it, so this is the delegated path with a generated selector rather than a
   * second mechanism. The disposer removes both.
   */
  attach(el: HTMLElement, def: Omit<MenuDefinition, 'on'>): () => void {
    attachCount += 1

    const attribute = `data-ctx-${attachCount}`

    el.setAttribute(attribute, '')

    const dispose = this.#registry.add({ ...def, on: `[${attribute}]` })
    let disposed = false

    const release = (): void => {
      if (disposed) return

      disposed = true
      this.#attached.delete(release)
      dispose()
      el.removeAttribute(attribute)
    }

    // Tracked so `destroy` can run it. Clearing the registry alone would leave
    // the marker attribute on the element for the life of the page.
    this.#attached.add(release)

    return release
  }

  /** Subscribe to a library event. Returns an unsubscribe function. */
  on(name: EventName, fn: Listener): () => void {
    return this.#emitter.on(name, fn)
  }

  /**
   * Open a menu. The nine steps below are the documented open sequence and
   * their order is asserted by the end to end suite.
   */
  async open(
    at: { x: number; y: number } | HTMLElement,
    def: MenuDefinition,
    el: HTMLElement,
    event?: Event,
  ): Promise<void> {
    if (this.#destroyed) return

    // Predicates, labellers and actions all receive a real `Event`, so an open
    // with no trigger gets a synthetic one rather than a hole in the contract.
    const trigger = event ?? new CustomEvent('ctxmenu:api')

    // Step 1: meta. A failure here aborts without suppressing the native menu,
    // because a menu that cannot describe its target should not replace the
    // browser's own.
    let meta: Meta

    try {
      meta = def.resolveMeta ? def.resolveMeta(el, trigger) : datasetMeta(el)
    } catch (error) {
      this.#emitter.emit('error', { el, meta: {}, error, originalEvent: event }, el)

      return
    }

    const ctx: Ctx = {
      el,
      meta,
      event: trigger,
      close: (reason) => this.close(reason ?? 'api'),
    }

    // Step 2: beforeopen. Cancelling it must leave the native menu alone, which
    // is the whole point: over a text selection or a link the browser's own
    // menu is the better answer.
    if (!this.#emitter.emit('beforeopen', { el, meta, originalEvent: event }, el)) return

    // Step 3: only now is the native menu suppressed.
    event?.preventDefault()

    // Step 4: never two menus at once.
    this.close('api')

    // A `beforeclose` listener kept the previous menu. Honour that rather than
    // stacking a second one on top of it.
    if (this.#state !== null) return

    this.#token += 1

    const token = this.#token
    const rtl = isRtl(el)
    const state: MenuState = {
      token,
      phase: 'pending',
      def,
      el,
      meta,
      ctx,
      event,
      anchor: at instanceof HTMLElement ? anchorOf(at, rtl) : at,
      offset: def.offset ?? this.#offset,
      rtl,
      menu: null,
      items: [],
      usedPopover: false,
      restoreFocus: this.#focusedElement(el),
      keyboard: null,
      submenus: [],
    }

    this.#state = state
    // Seed the pointer so the first safe triangle has a sane origin even if no
    // pointermove has been seen over the menu yet.
    this.#pointer = { ...state.anchor }
    this.#triangleOrigin = { ...state.anchor }
    this.#attachDismissal(el.ownerDocument)

    // Step 5: items, with a loading panel only for a resolver that really waits.
    const items = await this.#resolveItemList(state)

    if (items === null) return

    // Step 6: the list is handed out mutable, by reference, and it is a copy of
    // the definition's own array so a listener cannot corrupt the definition.
    this.#emitter.emit('beforeitems', { el, meta, items, originalEvent: event }, el)

    // Step 7: predicates, DOM, top layer.
    this.#idCount += 1

    const resolved = resolveItems(items, meta, ctx)
    const menu = renderMenu(resolved, {
      id: `ctx-menu-${this.#idCount}`,
      class: [...this.#className, ...classList(def.class)],
      variant: def.variant,
      header: def.header === undefined ? undefined : resolveLabel(def.header, meta, ctx),
      meta,
      ctx,
      onError: (error, item) => {
        this.#emitter.emit('error', { el, meta, item, error, originalEvent: event }, el)
      },
    })

    state.items = resolved
    this.#present(state, menu, 'open')

    // Step 8 happens inside `#present`: measure, place, apply.
    // Step 9: the menu is on screen and positioned before anyone hears about it.
    this.#emitter.emit('open', { el, meta, originalEvent: event }, el)
  }

  /**
   * Close the open menu.
   *
   * `beforeclose` may cancel this. `destroy` is the one caller that will not
   * take no for an answer.
   */
  close(reason: CloseReason = 'api'): void {
    this.#dismiss(reason, false)
  }

  /** Remove the root listener, close anything open, and forget every definition. */
  destroy(): void {
    if (this.#destroyed) return

    this.#destroyed = true
    this.#cancelLongPress?.()
    this.#dismiss('destroy', true)
    this.#root.removeEventListener('contextmenu', this.#onContextMenu)

    if (this.#longPress !== false) this.#root.removeEventListener('pointerdown', this.#onTouchStart)

    // A snapshot, because each disposer removes itself from the set as it runs.
    const attached = [...this.#attached]

    for (const release of attached) release()

    this.#registry.clear()
  }

  /**
   * Step 5, in full.
   *
   * Returns the items to render, or null when this open was superseded or
   * failed and has already shown what it is going to show.
   */
  async #resolveItemList(state: MenuState): Promise<Item[] | null> {
    const { def, meta, ctx, el, event, token } = state
    const produced = typeof def.items === 'function' ? def.items(meta, ctx) : def.items

    if (Array.isArray(produced)) return [...produced]

    let settled = false
    const pending = Promise.resolve(produced).then(
      (value) => {
        settled = true

        return value
      },
      (error: unknown) => {
        settled = true

        throw error
      },
    )

    for (let i = 0; i < MICROTASK_GRACE && !settled; i += 1) await Promise.resolve()

    if (this.#stale(token)) return null
    if (!settled) this.#present(state, renderLoading(), 'pending')

    try {
      const value = await pending

      return this.#stale(token) ? null : [...value]
    } catch (error) {
      if (this.#stale(token)) return null

      this.#emitter.emit('error', { el, meta, error, originalEvent: event }, el)
      this.#present(state, renderError('Could not load this menu'), 'error')

      return null
    }
  }

  /** Whether a newer open or a close has invalidated the work in flight. */
  #stale(token: number): boolean {
    return this.#destroyed || this.#state === null || this.#state.token !== token
  }

  /**
   * Mount one element as the current panel, replacing whatever was there.
   *
   * This is the single path onto the screen: the loading panel, the error panel
   * and the real menu are all complete `.ctx-menu` roots, so all three are
   * shown and placed by exactly the same code.
   */
  #present(state: MenuState, menu: HTMLElement, phase: Phase): void {
    if (state.menu !== null) {
      this.#closeSubmenus(1, false)
      state.keyboard?.destroy()
      state.keyboard = null
      this.#detachPanel(state.menu)
      hideMenu(state.menu, state.usedPopover)
      state.menu.remove()
    }

    state.menu = menu
    state.phase = phase
    this.#hoverRow = null
    menu.tabIndex = -1

    if (phase === 'open') this.#attachPanel(menu)

    state.el.ownerDocument.body.append(menu)
    state.usedPopover = showMenu(menu, this.#zIndexFallback)
    this.#position(state)

    if (phase !== 'open') return

    // The panel takes focus, not a row: opening a menu should not announce an
    // item nobody asked for. The first arrow key highlights the first row.
    menu.focus({ preventScroll: true })
    state.keyboard = new Keyboard(menu, this.#keyboardHandlers(0), { rtl: state.rtl })
  }

  /** Every listener a live panel needs, root or submenu, in one place. */
  #attachPanel(menu: HTMLElement): void {
    menu.addEventListener('click', this.#onPanelClick)
    menu.addEventListener('pointerover', this.#onPanelPointerOver)
    menu.addEventListener('pointermove', this.#onPanelPointerMove)
  }

  #detachPanel(menu: HTMLElement): void {
    menu.removeEventListener('click', this.#onPanelClick)
    menu.removeEventListener('pointerover', this.#onPanelPointerOver)
    menu.removeEventListener('pointermove', this.#onPanelPointerMove)
  }

  /** Step 8: measure, place, apply. */
  #position(state: MenuState): void {
    const menu = state.menu

    if (menu === null) return

    // offsetWidth/offsetHeight, not getBoundingClientRect. The entry animation
    // in @starting-style applies `scale: 0.97`, and getBoundingClientRect
    // reports the transformed box, so the menu measures 3% short at the exact
    // moment it is placed. Placement would then believe a menu fits where it
    // does not, and the real box overhangs the viewport a frame later. The
    // offset properties report the layout box and ignore transforms.
    const result = place({
      anchor: state.anchor,
      size: { width: menu.offsetWidth, height: menu.offsetHeight },
      viewport: viewportOf(menu),
      offset: state.offset,
      rtl: state.rtl,
    })

    // `place` returns physical viewport pixels and has already accounted for
    // the writing direction. A logical inset here would apply the direction a
    // second time and throw an RTL menu onto the wrong edge.
    menu.style.left = `${result.x}px`
    menu.style.top = `${result.y}px`
  }

  /** The close sequence. `force` ignores a `beforeclose` cancellation. */
  #dismiss(reason: CloseReason, force: boolean): void {
    const state = this.#state

    if (state === null) return

    const payload: EventPayload = {
      el: state.el,
      meta: state.meta,
      reason,
      originalEvent: state.event,
    }

    // A pending or failed panel never announced itself with `open`, so it does
    // not announce itself with `close` either.
    const announced = state.phase === 'open'

    if (announced) {
      const allowed = this.#emitter.emit('beforeclose', payload, state.el)

      if (!allowed && !force) return
    }

    // Submenus go first and while the state is still current, so each one can
    // announce its own `submenu:close` before the menu that owned them is gone.
    this.#cancelSubmenuTimers()
    this.#abandonPendingOpen()
    this.#closeSubmenus(1, false)

    this.#state = null
    this.#hoverRow = null
    // Any resolver still in flight for this menu is now stale.
    this.#token += 1
    this.#submenuToken += 1
    this.#detachDismissal()
    state.keyboard?.destroy()
    state.keyboard = null

    if (state.menu !== null) {
      this.#detachPanel(state.menu)
      hideMenu(state.menu, state.usedPopover)
      state.menu.remove()
    }

    this.#restoreFocus(state.restoreFocus)

    if (announced) this.#emitter.emit('close', payload, state.el)
  }

  /**
   * The element focus should return to when the menu closes.
   *
   * Whatever was focused when the menu opened, and the trigger itself when that
   * is nothing. The fallback is not cosmetic: WebKit blurs the focused element
   * on a right button `mousedown`, before `contextmenu` is dispatched, so by
   * the time this runs there is nothing focused at all and a menu opened with
   * the mouse would leave focus on `body` when it closed. Chromium and Firefox
   * keep the focus until later, so they never take this branch for a trigger
   * that was already focused.
   *
   * Falling back to the trigger is also what the menu pattern asks for, and it
   * costs nothing when the trigger cannot take focus: `focus()` on an element
   * that is not focusable does nothing, which leaves exactly the `body` that
   * would have been the outcome anyway.
   */
  #focusedElement(el: HTMLElement): HTMLElement {
    const active = el.ownerDocument.activeElement

    if (!(active instanceof HTMLElement)) return el
    if (active === el.ownerDocument.body) return el

    return active
  }

  #restoreFocus(previous: HTMLElement | null): void {
    if (previous === null || !previous.isConnected) return

    previous.focus({ preventScroll: true })
  }

  /**
   * Selection: `select`, then the close, then `action`.
   *
   * The action runs after the menu is gone on purpose. A slow action must never
   * leave a menu sitting in the top layer while it works, and a rejecting one
   * must not leave it there at all.
   */
  #select(item: ResolvedItem, originalEvent?: Event): void {
    const state = this.#state

    if (state === null || state.phase !== 'open') return

    const payload: EventPayload = {
      el: state.el,
      meta: state.meta,
      item,
      originalEvent: originalEvent ?? state.event,
    }

    if (!this.#emitter.emit('select', payload, state.el)) return

    const { meta, ctx, el } = state

    this.close('select')

    void this.#runAction(item, meta, ctx, el, payload)
  }

  async #runAction(
    item: ResolvedItem,
    meta: Meta,
    ctx: Ctx,
    el: HTMLElement,
    payload: EventPayload,
  ): Promise<void> {
    const action = actionOf(item)

    try {
      if (action !== undefined) await action(meta, ctx)
    } catch (error) {
      // A rejected action is reported, never rethrown into a listener stack and
      // never allowed to leave a menu open: the close already happened. `action`
      // does not also fire, because it reports success.
      this.#emitter.emit(
        'error',
        { el, meta, item, error, originalEvent: payload.originalEvent },
        el,
      )

      return
    }

    // After the await, not before it. `action` means the work finished, so a
    // listener can refresh a list or clear a spinner on it. Emitted before the
    // await it would carry no more information than `select` already did, and
    // an async action would have no completion signal at all.
    this.#emitter.emit('action', payload, el)
  }

  /** Every panel currently on screen, root first. */
  #panels(): HTMLElement[] {
    const state = this.#state

    if (state === null || state.menu === null) return []

    return [state.menu, ...state.submenus.map((level) => level.menu)]
  }

  #chainContains(node: Node): boolean {
    return this.#panels().some((panel) => panel.contains(node))
  }

  /** Whether a panel that handles its own keys owns this node. */
  #keyboardOwns(node: Node): boolean {
    const state = this.#state

    if (state === null) return false
    if (state.keyboard !== null && state.menu !== null && state.menu.contains(node)) return true

    return state.submenus.some((level) => level.menu.contains(node))
  }

  /** The depth of a panel in the chain, or -1 when it is not in it. */
  #depthOf(panel: HTMLElement): number {
    return this.#panels().indexOf(panel)
  }

  #itemsAt(depth: number): ResolvedItem[] {
    const state = this.#state

    if (state === null) return []
    if (depth === 0) return state.items

    return state.submenus[depth - 1]?.items ?? []
  }

  /** The row under an element, with the panel depth and the item it maps to. */
  #rowAt(target: Element): { row: HTMLElement; depth: number; item: ResolvedItem } | null {
    const state = this.#state

    if (state === null || state.phase !== 'open') return null

    const row = target.closest('.ctx-item')

    if (!(row instanceof HTMLElement)) return null

    const panel = row.closest('.ctx-menu')

    if (!(panel instanceof HTMLElement)) return null

    const depth = this.#depthOf(panel)

    if (depth === -1) return null

    const index = Number(row.dataset['ctxIndex'])
    const item = Number.isInteger(index) ? this.#itemsAt(depth)[index] : undefined

    return item === undefined ? null : { row, depth, item }
  }

  /**
   * What one panel's `Keyboard` calls back into.
   *
   * `Keyboard` deliberately knows nothing about the chain, so every decision
   * that depends on how deep its panel sits is made here instead.
   */
  #keyboardHandlers(depth: number): KeyboardHandlers {
    return {
      onHighlight: (row) => this.#onHighlight(row, depth),
      onActivate: (row) => this.#onActivate(row),
      onOpenSubmenu: (row) => {
        void this.#openSubmenu(row, depth, true)
      },
      // From a submenu this closes that submenu and returns focus to the row
      // that opened it. From the root it closes whatever a hover left open, and
      // does nothing at all when nothing is open.
      onCloseSubmenu: () => this.#closeSubmenus(Math.max(depth, 1), true),
      onClose: (reason) => this.#onKeyboardClose(reason),
    }
  }

  #onHighlight(row: HTMLElement, depth: number): void {
    const state = this.#state

    if (state === null) return

    // Moving along a panel abandons what that panel had open, unless the row
    // being highlighted is the one that owns it.
    const open = state.submenus[depth]

    if (open !== undefined && open.parentRow !== row) {
      this.#cancelSubmenuTimers()
      this.#closeSubmenus(depth + 1, false)
    }

    // A key moved the highlight, so whatever the pointer was last over is no
    // longer the current row and hovering it again is a new highlight.
    this.#hoverRow = null
    this.#emitHighlight(row)
  }

  /**
   * Announce that a row became the current one.
   *
   * One emitter behind both routes in, so a pointer hover and an arrow key
   * produce the same event with the same payload. The visual state needs no
   * attribute of its own: the keyboard path focuses the row and the pointer
   * path is a hover, and the stylesheet already covers both.
   */
  #emitHighlight(row: HTMLElement): void {
    const state = this.#state

    if (state === null) return

    const found = this.#rowAt(row)

    if (found === null) return

    this.#emitter.emit(
      'highlight',
      { el: state.el, meta: state.meta, item: found.item, originalEvent: state.event },
      state.el,
    )
  }

  #onActivate(row: HTMLElement): void {
    // Disabled rows are reachable on purpose, so the activation has to be the
    // thing that refuses, not the navigation.
    if (row.getAttribute('aria-disabled') === 'true') return

    const found = this.#rowAt(row)

    if (found === null) return

    this.#select(found.item)
  }

  /** `Escape` steps back one level. Anything else closes the whole chain. */
  #onKeyboardClose(reason: CloseReason): void {
    const state = this.#state

    if (state === null) return

    if (reason === 'escape' && state.submenus.length > 0) {
      this.#closeSubmenus(state.submenus.length, true)

      return
    }

    this.close(reason)
  }

  /**
   * Open the submenu of `row`, which lives in the panel at `depth`.
   *
   * The contract for the two submenu events: `submenu:open` fires immediately
   * before the panel is mounted, so every open that is not cancelled describes
   * a panel that really appeared, and `submenu:close` fires when that panel is
   * removed. The pair is therefore always balanced, and an open abandoned while
   * its resolver was in flight announces nothing at all. The price is that
   * cancelling `submenu:open` does not prevent an async resolver from having
   * run; announcing an open that may never mount would be worse.
   */
  async #openSubmenu(row: HTMLElement, depth: number, focusFirst: boolean): Promise<void> {
    const state = this.#state

    if (state === null || state.phase !== 'open') return

    const found = this.#rowAt(row)

    if (found === null || found.depth !== depth) return
    if (found.item.kind !== 'item' || !found.item.hasSubmenu || found.item.disabled) return

    this.#cancelSubmenuTimers()

    const existing = state.submenus[depth]

    // Already open for this row. Nothing to rebuild, only focus to move and any
    // grandchild to drop.
    if (existing !== undefined && existing.parentRow === row) {
      this.#closeSubmenus(depth + 2, false)

      if (focusFirst) existing.keyboard.focusFirst()

      return
    }

    this.#closeSubmenus(depth + 1, false)

    const source = submenuItemsOf(found.item)

    if (source === undefined) return

    const { token } = state

    this.#submenuToken += 1

    const openToken = this.#submenuToken

    this.#pendingOpen = { row, token: openToken }

    let list: Item[]

    try {
      list = typeof source === 'function' ? [...(await source(state.meta, state.ctx))] : [...source]
    } catch (error) {
      if (this.#staleSubmenu(token, openToken)) return

      this.#pendingOpen = null
      this.#emitter.emit(
        'error',
        { el: state.el, meta: state.meta, item: found.item, error, originalEvent: state.event },
        state.el,
      )

      return
    }

    // The pointer may have moved on, the menu may have closed, or a newer
    // submenu may have been asked for while the resolver was in flight. Any of
    // those and this open mounts nothing and says nothing.
    if (this.#staleSubmenu(token, openToken)) return

    this.#pendingOpen = null

    const payload: EventPayload = {
      el: state.el,
      meta: state.meta,
      item: found.item,
      originalEvent: state.event,
    }

    if (!this.#emitter.emit('submenu:open', payload, state.el)) return

    this.#idCount += 1

    const id = `ctx-menu-${this.#idCount}`
    const resolved = resolveItems(list, state.meta, state.ctx)
    const menu = renderMenu(resolved, {
      id,
      class: [...this.#className, ...classList(state.def.class)],
      variant: state.def.variant,
      meta: state.meta,
      ctx: state.ctx,
      onError: (error, item) => {
        this.#emitter.emit(
          'error',
          { el: state.el, meta: state.meta, item, error, originalEvent: state.event },
          state.el,
        )
      },
    })

    menu.tabIndex = -1
    menu.dataset['ctxDepth'] = String(depth + 1)
    this.#attachPanel(menu)
    state.el.ownerDocument.body.append(menu)

    const usedPopover = showMenu(menu, this.#zIndexFallback)

    this.#positionSubmenu(state, menu, row)

    // `aria-expanded` becomes true only once the panel exists, so a screen
    // reader is never told about a panel that was abandoned.
    row.setAttribute('aria-expanded', 'true')
    row.setAttribute('aria-controls', id)

    const keyboard = new Keyboard(menu, this.#keyboardHandlers(depth + 1), { rtl: state.rtl })

    state.submenus.push({
      parentRow: row,
      item: found.item,
      menu,
      items: resolved,
      keyboard,
      usedPopover,
    })

    // The wedge starts wherever the pointer was when this panel appeared.
    this.#triangleOrigin = { ...this.#pointer }

    if (focusFirst) keyboard.focusFirst()
  }

  /** Whether a submenu open in flight has been superseded or orphaned. */
  #staleSubmenu(token: number, openToken: number): boolean {
    return (
      this.#stale(token) || this.#submenuToken !== openToken || this.#state?.phase !== 'open'
    )
  }

  /**
   * Close every level from `from` down, deepest first.
   *
   * `from` counts the root as level 0, so `1` closes every submenu. Descendants
   * go before their parents, which makes `submenu:close` arrive in the order a
   * listener would draw the chain shrinking in.
   */
  #closeSubmenus(from: number, focusParent: boolean): void {
    const state = this.#state

    if (state === null) return

    const level = Math.max(from, 1)

    if (state.submenus.length < level) return

    const removed = state.submenus.splice(level - 1)
    const hovered = this.#hoverRow

    // The current row went with the panel it was in, so the next pointerover
    // anywhere is a fresh highlight.
    if (hovered !== null && removed.some((entry) => entry.menu.contains(hovered))) {
      this.#hoverRow = null
    }

    for (const submenu of [...removed].reverse()) {
      submenu.keyboard.destroy()
      this.#detachPanel(submenu.menu)
      hideMenu(submenu.menu, submenu.usedPopover)
      submenu.menu.remove()
      submenu.parentRow.setAttribute('aria-expanded', 'false')
      submenu.parentRow.removeAttribute('aria-controls')

      this.#emitter.emit(
        'submenu:close',
        { el: state.el, meta: state.meta, item: submenu.item, originalEvent: state.event },
        state.el,
      )
    }

    const shallowest = removed[0]

    if (focusParent && shallowest !== undefined && shallowest.parentRow.isConnected) {
      shallowest.parentRow.focus({ preventScroll: true })
    }
  }

  /**
   * Place a submenu beside the panel that owns it.
   *
   * The `avoid` rect is the parent panel and not just the parent row: the row
   * is inset by the panel's own padding and border, so flanking the row alone
   * would tuck the submenu underneath its parent. The row still supplies the
   * vertical anchor, so the panel lines up with the item that opened it, and
   * `place` still owns every flip and clamp decision.
   */
  #positionSubmenu(state: MenuState, menu: HTMLElement, row: HTMLElement): void {
    const parent = row.closest('.ctx-menu')
    // A rect is the right measurement for both of these, because both are
    // already on screen at full scale: the parent panel is the region to flank
    // and the row is the vertical anchor. Neither is the panel being placed.
    const avoid = parent instanceof HTMLElement ? rectOf(parent) : rectOf(row)
    const anchor = rectOf(row)

    const result = place({
      anchor: { x: anchor.x, y: anchor.y },
      // offsetWidth/offsetHeight, for the same reason the root panel is
      // measured that way in `#position`: this panel is mid entry animation,
      // the `@starting-style` rule has `scale: 0.97` on every `.ctx-menu`, and
      // getBoundingClientRect reports the transformed box. Measured 3% short a
      // submenu is believed to fit beside the parent panel when it does not,
      // and it settles a few pixels past the viewport's inline end.
      size: { width: menu.offsetWidth, height: menu.offsetHeight },
      viewport: viewportOf(menu),
      offset: { x: state.offset.x, y: 0 },
      avoid,
      rtl: state.rtl,
    })

    menu.style.left = `${result.x}px`
    menu.style.top = `${result.y}px`
  }

  #clearOpenTimer(): void {
    this.#pendingHover = null

    if (this.#submenuOpenTimer === null) return

    clearTimeout(this.#submenuOpenTimer)
    this.#submenuOpenTimer = null
  }

  /**
   * Start, or restart, the hover delay for whichever row is pending.
   *
   * The delay does not simply expire. When it fires, a row that the pointer is
   * merely crossing on its way to an open submenu gets another delay instead of
   * a panel. Without that, the safe triangle is only half a fix: it holds the
   * close off, and then a sibling that has a submenu of its own reaches its own
   * 120ms first and tears the panel down anyway, which is the diagonal traverse
   * failing for a different reason. The playground shows this exactly, where
   * `Recent activity` sits directly under `Share`.
   *
   * The deferral ends by itself, and on the only signal that means anything:
   * whether the pointer moved while it was waiting. A pointer still travelling
   * through the wedge is traversing, so it waits again. A pointer that has
   * stopped has chosen the row it stopped on, so that row opens. No pointer
   * ever gets stuck in the wedge, and the rule needs no sampling rate: it is a
   * question asked once per delay, not once per event.
   */
  #armSubmenuOpen(): void {
    const hover = this.#pendingHover

    if (hover === null) return

    if (this.#submenuOpenTimer !== null) clearTimeout(this.#submenuOpenTimer)

    const armedAt = { ...this.#pointer }

    this.#submenuOpenTimer = setTimeout(() => {
      this.#submenuOpenTimer = null

      if (this.#stillTraversing(armedAt, hover)) {
        this.#armSubmenuOpen()

        return
      }

      this.#pendingHover = null
      void this.#openSubmenu(hover.row, hover.depth, false)
    }, SUBMENU_OPEN_DELAY)
  }

  /** Is the pointer moving through this row towards a submenu already open? */
  #stillTraversing(
    armedAt: { x: number; y: number },
    hover: { row: HTMLElement; depth: number },
  ): boolean {
    const state = this.#state

    if (state === null) return false

    const moved = this.#pointer.x !== armedAt.x || this.#pointer.y !== armedAt.y

    if (!moved) return false

    const open = state.submenus[hover.depth]

    if (open === undefined || open.parentRow === hover.row) return false

    return inTriangle(this.#pointer, this.#triangleOrigin, rectOf(open.menu))
  }

  #cancelPendingClose(): void {
    const pending = this.#pendingClose

    if (pending === null) return

    if (pending.timer !== null) clearTimeout(pending.timer)

    this.#pendingClose = null
  }

  #cancelSubmenuTimers(): void {
    this.#clearOpenTimer()
    this.#cancelPendingClose()
  }

  /** Give up on a submenu whose resolver has not come back yet. */
  #abandonPendingOpen(): void {
    if (this.#pendingOpen === null) return

    this.#pendingOpen = null
    this.#submenuToken += 1
  }

  #scheduleSubmenuClose(from: number): void {
    if (this.#pendingClose !== null && this.#pendingClose.from === from) return

    this.#cancelPendingClose()

    const pending: PendingClose = { from, timer: null }

    this.#pendingClose = pending
    this.#armSubmenuClose(pending)
  }

  #armSubmenuClose(pending: PendingClose): void {
    pending.timer = setTimeout(() => {
      pending.timer = null

      if (this.#pendingClose === pending) this.#pendingClose = null

      this.#closeSubmenus(pending.from, false)
    }, SUBMENU_CLOSE_DELAY)
  }

  /**
   * Hover intent, one row at a time.
   *
   * `pointerover` fires once per row entered rather than once per pixel, which
   * is why the open and close decisions live here and only the safe triangle
   * lives in `pointermove`.
   */
  readonly #onPanelPointerOver = (event: Event): void => {
    const state = this.#state

    if (state === null || state.phase !== 'open') return

    const point = pointerPointOf(event)

    if (point !== null) this.#pointer = point

    const target = event.target

    if (!(target instanceof Element)) return

    const panel = target.closest('.ctx-menu')

    if (!(panel instanceof HTMLElement)) return

    const depth = this.#depthOf(panel)

    if (depth === -1) return

    // Arriving anywhere in a panel cancels a close scheduled for that panel or
    // for a shallower one: the pointer plainly did not abandon it.
    if (this.#pendingClose !== null && this.#pendingClose.from <= depth) this.#cancelPendingClose()

    const row = target.closest('.ctx-item')

    if (!(row instanceof HTMLElement)) {
      // The pointer is on the panel itself. Nothing is the current row any
      // more, so coming back to the row it just left highlights it again.
      this.#hoverRow = null

      return
    }

    // Pointer parity with the keyboard: `highlight` means "this row is the
    // current one", however it became current.
    if (this.#hoverRow !== row) {
      this.#hoverRow = row
      this.#emitHighlight(row)
    }

    const open = state.submenus[depth]

    if (open !== undefined && open.parentRow === row) {
      this.#cancelSubmenuTimers()
      // Re-anchor the wedge: the traverse starts from wherever the pointer is
      // on the owning row, not from where it first arrived.
      this.#triangleOrigin = { ...this.#pointer }

      return
    }

    if (this.#pendingOpen !== null && this.#pendingOpen.row !== row) this.#abandonPendingOpen()

    this.#clearOpenTimer()

    const opensSubmenu =
      row.getAttribute('aria-haspopup') === 'menu' && row.getAttribute('aria-disabled') !== 'true'

    if (opensSubmenu) {
      this.#pendingHover = { row, depth }
      this.#armSubmenuOpen()
    }

    if (open !== undefined) this.#scheduleSubmenuClose(depth + 1)
  }

  /**
   * The safe triangle, applied.
   *
   * While the pointer is inside the wedge from where the traverse started to
   * the near corners of the open submenu, the scheduled close is held. The
   * moment it leaves the wedge the close is armed again, so a pointer that
   * wanders off still closes the panel on time.
   */
  readonly #onPanelPointerMove = (event: Event): void => {
    const point = pointerPointOf(event)

    if (point === null) return

    this.#pointer = point

    const pending = this.#pendingClose

    if (pending === null) return

    const level = this.#state?.submenus[pending.from - 1]

    if (level === undefined) {
      this.#cancelPendingClose()

      return
    }

    if (inTriangle(point, this.#triangleOrigin, rectOf(level.menu))) {
      if (pending.timer !== null) {
        clearTimeout(pending.timer)
        pending.timer = null
      }

      return
    }

    if (pending.timer === null) this.#armSubmenuClose(pending)
  }

  #attachDismissal(doc: Document): void {
    this.#detachDismissal()
    this.#dismissalDocument = doc

    // Capture, so an application that stops these events on its own container
    // cannot strand an open menu.
    doc.addEventListener('keydown', this.#onKeyDown, true)
    doc.addEventListener('pointerdown', this.#onPointerDown, true)

    if (this.#closeOnScroll) doc.addEventListener('scroll', this.#onScroll, true)

    doc.defaultView?.addEventListener('blur', this.#onBlur)
  }

  #detachDismissal(): void {
    const doc = this.#dismissalDocument

    if (doc === null) return

    this.#dismissalDocument = null
    doc.removeEventListener('keydown', this.#onKeyDown, true)
    doc.removeEventListener('pointerdown', this.#onPointerDown, true)
    doc.removeEventListener('scroll', this.#onScroll, true)
    doc.defaultView?.removeEventListener('blur', this.#onBlur)
  }

  readonly #onContextMenu = (event: Event): void => {
    if (this.#destroyed) return

    // Another handler already claimed this event, for example a nested
    // instance or the application itself.
    if (event.defaultPrevented) return

    const target = event.target

    // A right click inside the open menu belongs to the menu, not to whatever
    // definition happens to match the element under it.
    if (target instanceof Node && this.#chainContains(target)) return

    const match = this.#registry.match(target, this.#root)

    if (match === null) return

    void this.open(pointOf(event, match.el, isRtl(match.el)), match.def, match.el, event)
  }

  readonly #onPanelClick = (event: Event): void => {
    const target = event.target

    if (!(target instanceof Element)) return

    const found = this.#rowAt(target)

    if (found === null) return

    // Disabled rows stay focusable and clickable so a screen reader can reach
    // them; the click simply does nothing.
    if (found.row.getAttribute('aria-disabled') === 'true') return

    if (found.item.kind === 'item' && found.item.hasSubmenu) {
      void this.#openSubmenu(found.row, found.depth, false)

      return
    }

    this.#select(found.item, event)
  }

  /**
   * Escape from outside the chain.
   *
   * A panel with a `Keyboard` handles its own Escape, because only that path
   * knows which level to step back to. This capture listener is the fallback
   * for focus that has left the menu entirely, and for the transient loading
   * and error panels, which take no keys of their own.
   */
  readonly #onKeyDown = (event: Event): void => {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Escape') return

    const target = event.target

    if (target instanceof Node && this.#keyboardOwns(target)) return

    event.preventDefault()
    event.stopPropagation()
    this.close('escape')
  }

  readonly #onPointerDown = (event: Event): void => {
    const target = event.target

    if (target instanceof Node && this.#chainContains(target)) return

    this.close('outside')
  }

  /**
   * Close only for a scroll that actually moves the menu's anchor.
   *
   * The menu is placed at a viewport point, so it goes stale exactly when the
   * element it was opened on moves under it. That happens when the page scrolls
   * or when a scroll container the trigger sits inside scrolls, and never when
   * some unrelated region elsewhere in the document scrolls.
   *
   * Closing on any scroll at all is not a harmless over reaction, it is a bug
   * with real consequences: a live log, a chat pane or a virtualised table that
   * scrolls itself while the user is reading a menu would dismiss that menu
   * mid gesture. The playground's own event log does precisely this, and it is
   * what `events.spec.ts` pins down.
   *
   * `contains` is the whole test, and it reads the document case for free: a
   * scroll of the viewport is reported on the `Document` or on
   * `documentElement`, and both contain every connected element.
   */
  readonly #onScroll = (event: Event): void => {
    const state = this.#state

    if (state === null) return

    const target = event.target

    if (target instanceof Node) {
      // The menu scrolls itself when it is taller than the viewport allows.
      if (this.#chainContains(target)) return
      if (!target.contains(state.el)) return
    }

    this.close('scroll')
  }

  readonly #onBlur = (): void => {
    this.close('blur')
  }

  /**
   * Touch: a hold opens the menu, movement or a lift cancels it.
   *
   * The move and end listeners exist only for the duration of one gesture, so
   * the instance still owns exactly one permanent delegated listener per event
   * type on its root.
   */
  readonly #onTouchStart = (event: Event): void => {
    if (this.#destroyed || this.#longPress === false) return
    if (!(event instanceof PointerEvent) || event.pointerType !== 'touch') return

    const match = this.#registry.match(event.target, this.#root)

    if (match === null) return

    this.#cancelLongPress?.()

    const start = { x: event.clientX, y: event.clientY }
    const doc = match.el.ownerDocument
    const onMove = (moveEvent: Event): void => {
      if (!(moveEvent instanceof PointerEvent)) return

      const travelled = Math.hypot(moveEvent.clientX - start.x, moveEvent.clientY - start.y)

      if (travelled > LONG_PRESS_SLOP) this.#cancelLongPress?.()
    }
    const onEnd = (): void => {
      this.#cancelLongPress?.()
    }

    const timer = setTimeout(() => {
      this.#cancelLongPress?.()
      void this.open(start, match.def, match.el, event)
    }, this.#longPress)

    this.#cancelLongPress = () => {
      this.#cancelLongPress = null
      clearTimeout(timer)
      doc.removeEventListener('pointermove', onMove, true)
      doc.removeEventListener('pointerup', onEnd, true)
      doc.removeEventListener('pointercancel', onEnd, true)
    }

    doc.addEventListener('pointermove', onMove, true)
    doc.addEventListener('pointerup', onEnd, true)
    doc.addEventListener('pointercancel', onEnd, true)
  }
}

/** The functional way to build an instance, for consumers who prefer it. */
export function createContextMenu(options?: ContextMenuOptions): ContextMenu {
  return new ContextMenu(options)
}
